import { Switch } from "@/components/ui/switch";
import SubscriptionList from "@/features/user/components/subscription-list/subscription-list";
import { useLng } from "@/hooks/use-lng";
import { appTitleHead } from "@/lib/app-document-title";
import { useTranslation } from "@/lib/i18n/client";
import { GET_SUBSCRIPTION_HISTORY } from "@/query/getSubscriptionHistory";
import { useConfigStore } from "@/store/use-config-store";
import { Subscription } from "@/types/subscriptionInterfaces";
import { useLocalStorage } from "@/utils/useLocalStorage";
import { cardVariants } from "@/utils/variants";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import request from "graphql-request";
import { AnimatePresence, motion } from "motion/react";
import { useAuth } from "oidc-react";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useShallow } from "zustand/react/shallow";

type SubscriptionHistoryResponse = {
  member?: {
    subscriptionHistory: Subscription[];
  };
};

type SubscriptionsSearch = {
  isSuccess?: string;
  Error?: string;
};

export const Route = createFileRoute("/_app/account/_auth/subscriptions")({
  validateSearch: (search: Record<string, unknown>): SubscriptionsSearch => {
    const isSuccess =
      typeof search.isSuccess === "string" && search.isSuccess.length > 0
        ? search.isSuccess
        : undefined;
    const Error =
      typeof search.Error === "string" && search.Error.length > 0 ? search.Error : undefined;
    return {
      ...(isSuccess !== undefined ? { isSuccess } : {}),
      ...(Error !== undefined ? { Error } : {})
    };
  },
  head: () => appTitleHead("account-subscription-title"),
  component: SubscriptionsPage
});

function SubscriptionsPage() {
  const { userData } = useAuth();
  const [settings] = useConfigStore(useShallow((state) => [state.settings]));
  const lng = useLng();
  const { t } = useTranslation(lng, "translation");
  const [isChecked, setChecked] = useLocalStorage("isCheckedSubscriptions", true);

  const navigate = useNavigate();
  const search = Route.useSearch();
  const isSuccessParam = search.isSuccess === "True";
  const errorParam = search.Error;

  const getSubscriptionHistory = async () => {
    const url = `${settings?.membersApiUri}/graphql`;
    const headers = {
      authorization: userData?.access_token ? `Bearer ${userData?.access_token}` : ""
    };
    return await request<SubscriptionHistoryResponse>(
      url,
      GET_SUBSCRIPTION_HISTORY,
      undefined,
      headers
    );
  };

  const { data: subscriptionHistory } = useQuery<SubscriptionHistoryResponse>({
    queryKey: ["subscriptionHistory"],
    queryFn: getSubscriptionHistory,
    enabled: !!userData?.access_token && !!settings?.membersApiUri
  });

  const sortedSubscription = useMemo(() => {
    const sorted = subscriptionHistory?.member?.subscriptionHistory.sort(
      (a: Subscription, b: Subscription) =>
        a.uniqueIdentifier !== null && a.registrationDate > b.registrationDate ? -1 : 1
    );

    if (!isChecked && sorted) {
      return sorted.filter(
        (subscription: Subscription) =>
          subscription.status !== "Cancelled" &&
          subscription.status !== "Aborted" &&
          subscription.status !== "PaidInFull"
      );
    }

    return sorted;
  }, [subscriptionHistory, isChecked]);

  const deleteSearchParams = () => {
    void navigate({
      to: "/account/subscriptions",
      search: {},
      replace: true
    });
  };

  useEffect(() => {
    if (isSuccessParam) {
      toast.success(t("account-subscription-change-card-success"), {
        duration: 5000,
        id: "changeCard"
      });
      deleteSearchParams();
    } else if (errorParam) {
      toast.error(t("account-subscription-change-card-error"), {
        duration: 5000,
        id: "changeCard"
      });
      deleteSearchParams();
    }
  }, [errorParam, isSuccessParam]);

  return (
    <div className="flex flex-col">
      <h1 className="hidden text-left md:block">{t("subscription-header")}</h1>
      <p>{t("subscription-description")}</p>
      <div className="my-4 flex items-center justify-between">
        <h2 className="p-0">{t("account-subscription-title")}</h2>
        <span className="flex items-center justify-between gap-3 p-0 text-sm font-semibold">
          {t("show-inactive")}
          <Switch
            checked={isChecked}
            onCheckedChange={(newChecked: boolean) => setChecked(newChecked)}
            aria-label={isChecked ? t("show-inactive-aria-on") : t("show-inactive-aria-off")}
          />
        </span>
      </div>
      <div className="grid w-full items-center gap-4 overflow-hidden md:max-w-full">
        <AnimatePresence>
          {sortedSubscription &&
            sortedSubscription.map((subscription: Subscription, i: number) => (
              <motion.div
                key={subscription.subscriptionId}
                custom={i}
                animate="visible"
                initial="hidden"
                exit="hidden"
                variants={cardVariants}
              >
                <SubscriptionList subscriptionId={subscription.subscriptionId} />
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
